import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../parts/modal-new";
import {
  buildRecordRows,
  emailFormatError,
  fromAddressError,
} from "../../../../utils/email-sending";
import CopyValueButton from "../components/CopyValueButton";

const id = "modal-setup-email";
const ACTION = "/actions/automation-sending";
const MODES = ["domain", "google", "msb"];

// The form theme draws a check (aria-invalid="false") or a cross (aria-invalid="true") inside the
// field: only once something was typed, so an empty field with its placeholder shows neither
const validityOf = (value, errorKey, touched) =>
  touched && value.trim() ? Boolean(errorKey) : undefined;

const RecordsTable = ({ rows }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailSending.records.${key}`);
  return (
    <table className="es-records">
      <thead>
        <tr>
          <th>{tr("columns.purpose")}</th>
          <th>{tr("columns.type")}</th>
          <th>{tr("columns.host")}</th>
          <th>{tr("columns.value")}</th>
          <th className="es-copy-cell">
            <span className="sr-only">{tr("copy")}</span>
          </th>
          <th>{tr("columns.status")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td>{tr(`purposes.${row.purposeKey}`)}</td>
            <td>{row.type}</td>
            <td className="es-copyable-text">{row.host}</td>
            <td className="es-copyable-text">{row.value}</td>
            <td className="es-copy-cell">
              <CopyValueButton value={row.value} />
            </td>
            <td>
              <span className={`es-chip es-chip--${row.status}`}>
                {tr(`status.${row.status}`)}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

RecordsTable.propTypes = { rows: PropTypes.array.isRequired };

const DomainRecords = ({ settings }) => {
  const { t } = useTranslation("coaching-portal");
  const [askingInstructions, setAskingInstructions] = useState(false);
  const tr = (key, options) => t(`emailSending.records.${key}`, options);
  const secondary = { "data-button-type": "secondary", formNoValidate: true };

  return (
    <section className="es-records-box">
      <header>
        <div>
          <h4 className="es-records-title">
            {tr("title", { domain: settings.domain })}
          </h4>
          <p>{tr("help")}</p>
        </div>
        <div className="es-records-actions">
          <button
            type="button"
            data-button-type="secondary"
            onClick={() => setAskingInstructions((open) => !open)}
          >
            {tr("emailInstructions")}
          </button>
          <button
            type="submit"
            data-button-type="primary"
            formNoValidate
            formAction={`${ACTION}/check`}
          >
            {tr("checkAgain")}
          </button>
        </div>
      </header>

      {askingInstructions ? (
        <div className="es-instructions">
          <label htmlFor="es-instructions-email">
            {t("emailSending.instructions.label")}
          </label>
          <input
            id="es-instructions-email"
            type="email"
            name="instructionsEmail"
            placeholder={t("emailSending.instructions.placeholder")}
          />
          <button
            type="submit"
            {...secondary}
            formAction={`${ACTION}/instructions`}
          >
            {t("emailSending.instructions.send")}
          </button>
        </div>
      ) : null}

      <RecordsTable rows={buildRecordRows(settings)} />

      <footer>
        <p>
          {settings.state === "verified"
            ? tr("verifiedNote")
            : tr("fallbackNote")}
        </p>
        <button
          type="submit"
          data-button-type="text"
          formNoValidate
          formAction={`${ACTION}/change-domain`}
          onClick={(event) => {
            if (
              !window.confirm(
                tr("changeDomainConfirm", { domain: settings.domain })
              )
            ) {
              event.preventDefault();
            }
          }}
        >
          {tr("changeDomain")}
        </button>
      </footer>
    </section>
  );
};

DomainRecords.propTypes = { settings: PropTypes.object.isRequired };

const OptionCard = ({ mode, selected, disabled, onSelect }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailSending.options.${mode}.${key}`);
  return (
    <label
      className={`es-option${selected ? " es-option--selected" : ""}`}
      aria-disabled={disabled || undefined}
    >
      <input
        type="radio"
        name="mode"
        value={mode}
        className="sr-only"
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(mode)}
      />
      <i className="es-option__box" aria-hidden="true" />
      <strong>{tr("title")}</strong>
      <span>{tr("body")}</span>
      <em>{tr("tag")}</em>
    </label>
  );
};

OptionCard.propTypes = {
  mode: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const SetupEmail = ({ settings, ownerEmail }) => {
  const { t } = useTranslation("coaching-portal");
  const [mode, setMode] = useState("domain");
  const [fromEmail, setFromEmail] = useState(settings?.fromEmail ?? "");
  const [touched, setTouched] = useState(false);
  const [replyTo, setReplyTo] = useState(settings?.replyTo ?? "");
  const [replyToTouched, setReplyToTouched] = useState(false);
  const tr = (key, options) => t(`emailSending.${key}`, options);

  if (!settings) {
    return (
      <Modal name={id} key={id} title={tr("title")} className="email-setup">
        <div className="body">
          <p>{tr("errors.loadFailed")}</p>
        </div>
      </Modal>
    );
  }

  const usingDomain = mode === "domain";
  const replyToErrorKey = emailFormatError(replyTo);
  const errorKey = usingDomain
    ? fromAddressError(fromEmail, settings.domain)
    : null;

  return (
    <form
      action={`${ACTION}/save`}
      method="POST"
      data-noreset
      id="setup-email-form"
    >
      <Modal name={id} key={id} title={tr("title")} className="email-setup">
        <div className="body">
          {settings.paused ? (
            <div className="es-banner es-banner--danger">
              <p>
                <strong>{tr("paused.title")}</strong>
                {" · "}
                {tr("paused.body", { reason: settings.paused.reason })}
              </p>
            </div>
          ) : null}

          <hgroup>
            <h4>{tr("intro.title")}</h4>
            <p>{tr("intro.body")}</p>
          </hgroup>

          <fieldset className="es-options">
            <legend className="sr-only">{tr("intro.title")}</legend>
            {MODES.map((option) => (
              <OptionCard
                key={option}
                mode={option}
                selected={mode === option}
                disabled={option === "google"}
                onSelect={setMode}
              />
            ))}
          </fieldset>

          <section className="es-fields">
            <label>
              <span className="es-field-label">{tr("fields.fromName")}</span>
              <input
                type="text"
                name="fromName"
                maxLength={100}
                placeholder={tr("fields.fromNamePlaceholder")}
                defaultValue={settings.fromName ?? ""}
              />
            </label>
            {usingDomain ? (
              <label>
                <span className="es-field-label">
                  {tr("fields.fromAddress")}
                </span>
                <input
                  type="email"
                  name="fromEmail"
                  value={fromEmail}
                  aria-invalid={validityOf(fromEmail, errorKey, touched)}
                  onChange={(event) => {
                    setFromEmail(event.target.value);
                    setTouched(true);
                  }}
                  placeholder="contact@yourgym.com"
                />
                {errorKey && touched ? (
                  <small className="es-error" role="alert">
                    {tr(`errors.${errorKey}`, { domain: settings.domain })}
                  </small>
                ) : null}
              </label>
            ) : null}
            <label>
              <span className="es-field-label">{tr("fields.replyTo")}</span>
              <input
                type="email"
                name="replyTo"
                value={replyTo}
                aria-invalid={validityOf(
                  replyTo,
                  replyToErrorKey,
                  replyToTouched
                )}
                onChange={(event) => {
                  setReplyTo(event.target.value);
                  setReplyToTouched(true);
                }}
                placeholder={ownerEmail}
              />
              {replyToErrorKey && replyToTouched ? (
                <small className="es-error" role="alert">
                  {tr(`errors.${replyToErrorKey}`)}
                </small>
              ) : null}
            </label>
          </section>

          {usingDomain && settings.domain ? (
            <DomainRecords settings={settings} />
          ) : null}
          {usingDomain && !settings.domain ? (
            <p className="es-hint">{tr("domainHint")}</p>
          ) : null}

          <section className="es-footer-fields">
            <label>
              <span className="es-field-label">
                {tr("fields.postalAddress")}
              </span>
              <input
                type="text"
                name="postalAddress"
                maxLength={300}
                defaultValue={settings.postalAddress ?? ""}
                placeholder={tr("fields.postalPlaceholder")}
              />
            </label>
            <div>
              <h5>{tr("unsubscribe.title")}</h5>
              <p>{tr("unsubscribe.body")}</p>
            </div>
          </section>
        </div>

        <footer className="foot">
          {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
          <a
            href="/actions/refresh?modal="
            role="button"
            data-button-type="secondary"
            data-action="cancel"
          >
            {tr("actions.cancel")}
          </a>
          <button
            type="submit"
            data-button-type="primary"
            disabled={Boolean(errorKey || replyToErrorKey)}
          >
            {tr("actions.save")}
          </button>
        </footer>
      </Modal>
    </form>
  );
};

SetupEmail.propTypes = {
  settings: PropTypes.object,
  ownerEmail: PropTypes.string,
};

export default SetupEmail;
